/**
 * useEntitlementsSettled — shared "is the org's full entitlements payload
 * actually here" gate used by ProtectedRoute (rabbit loader) and AppSidebar
 * (skeleton). Both must release together; if ProtectedRoute releases first,
 * the user sees the sidebar's own skeleton flash in for a frame before the
 * real menu lands. If AppSidebar releases first, the partial menu (only
 * ALWAYS_ENABLED_FEATURES) renders before the plan features arrive — which
 * is the "few default features then more" stagger team members report.
 *
 * Settled means ANY of:
 *   - error    → fall back to whatever the hook returned, don't shimmer forever
 *   - no-sub   → confirmed null subscription (trial / fresh org)
 *   - planFeature observed → at least one key outside ALWAYS_ENABLED_FEATURES
 *   - 1500ms safety after data first became defined → catches the edge
 *     case where backend returns a sub with no entitlements.features at all
 */
import React from 'react';
import { useCurrentSubscription } from '@/hooks/useCurrentSubscription';
import { useOrganizationEntitlements, ALWAYS_ENABLED_FEATURES } from '@/hooks/useFeatureLimits';

const SAFETY_MS = 1500;

export function useEntitlementsSettled(enabled: boolean = true): boolean {
  const { data: entitlements, isError: entitlementsError } = useOrganizationEntitlements();
  const { data: rawSubscription } = useCurrentSubscription({ enabled });
  const [settled, setSettled] = React.useState(false);

  React.useEffect(() => {
    if (!enabled) return;
    if (settled) return;
    if (entitlementsError) { setSettled(true); return; }
    // Don't settle while the subscription query is still in-flight on the
    // initial fetch — `data` undefined means the response hasn't landed.
    // Without this guard, a fast response that fires the effect before
    // entitlements were derived could prematurely settle on the safety timer.
    if (rawSubscription === undefined) return;
    const featureMap = entitlements?.entitlements?.features || {};
    const hasPlanFeature = Object.keys(featureMap).some(
      (k) => !(k in ALWAYS_ENABLED_FEATURES),
    );
    if (rawSubscription === null || hasPlanFeature) { setSettled(true); return; }
    // Subscription is defined but carries no plan features — wait briefly in
    // case a follow-up refetch fills them in, then give up and render with
    // whatever we have so users don't shimmer forever on a degenerate plan.
    const t = setTimeout(() => setSettled(true), SAFETY_MS);
    return () => clearTimeout(t);
  }, [entitlements, rawSubscription, entitlementsError, settled, enabled]);

  // When the hook isn't applicable (super admin, no org), report settled so
  // callers don't block on a state that will never resolve.
  return !enabled ? true : settled;
}
